import { useState } from 'react';
import {
  LayoutDashboard, Boxes, Building2, Server, FolderKanban,
  AlertTriangle, FileText, FileLock2, RefreshCw, Settings, Sparkles, Cable,
  ClipboardCheck, Mail, Bot, Wallet, ShieldCheck, Activity, QrCode,
  GitCommit, User, CalendarDays, Wand2, FileBarChart2, CreditCard, Crown,
  LifeBuoy, Package, Shield, Newspaper, Calculator, Route as RouteIcon, HardHat,
  ChevronDown, GraduationCap, HeartPulse, Rss, AudioLines, TrainFront, Inbox, Waves, Home, Radio, Camera, PencilRuler, Zap,
  Stethoscope, RadioTower, Compass, Coins, Landmark, Leaf, Rocket, Layers, Megaphone, Briefcase,
  Brain, Box, Ruler, StickyNote as StickyNote_,
  Cpu, Users, TrendingUp, Copy, Clock, Film, Video, Mic, Clapperboard, ScanEye,
  Globe, Music, Keyboard, Monitor, FileDown, Wifi, BookOpen, Scale,
  Map, Building2 as Building2Icon, MessageSquare, ShoppingCart, Award,
  Star,
} from 'lucide-react';
import type { Route } from '../../App.js';
import { useLocale } from '../../i18n/LocaleContext.js';
import { useFeatureRegistry } from '../../hooks/useFeatureRegistry.js';
import { useWorkspacePrefs } from '../../hooks/useWorkspacePrefs.js';
import { FeatureBadge } from '../FeatureBadge.js';
import { LogoMark } from '../branding/LogoMark.js';
import { FounderEditionBadge } from '../branding/FounderEditionBadge.js';
import { useFounderMode } from '../../stores/founderMode.store.js';
import { MiniEcgMonitor } from '../branding/MiniEcgMonitor.js';
import { LastMileTracer } from '../capture/LastMileTracer.js';
import { getBrand } from '@bim/shared/config/brand.config.js';
import { useAuthStore } from '../../stores/auth.store.js';
import { useLicense } from '../../hooks/useLicense.js';
import { useGatekeeperAlertStore } from '../../stores/gatekeeperAlert.store.js';
import { useDashboardStats } from '../../hooks/useDashboard.js';
import { useFeatureFlagsStore } from '../../stores/featureFlags.store.js';
import { useFavorites } from '../../stores/favorites.store.js';

// ── Tier → Edition display map ────────────────────────────────────────────────
const TIER_EDITION: Record<string, { label: string; color: string }> = {
  designer:   { label: 'Enterprise Edition', color: 'rgba(252,211,77,0.78)' },
  contractor: { label: 'Contractor Edition', color: 'rgba(94,234,212,0.65)' },
  technician: { label: 'Technician Edition', color: 'rgba(148,172,210,0.45)' },
};

// ── Tier label for credential line ───────────────────────────────────────────
const TIER_LABEL: Record<string, string> = {
  designer:   'Enterprise',
  contractor: 'Contractor',
  technician: 'Technician',
  trial:      'Trial',
};

function formatBicsiDisplay(raw: string): string {
  if (raw === 'MASTER-ADMIN') return 'MASTER';
  if (raw === 'dev-preview')  return 'DEV';
  return raw.replace(/^#/, '').toUpperCase();
}

interface SidebarProps {
  route: Route;
  onNavigate: (r: Route) => void;
}

type NavItem = { id: Route | string; routeId?: Route; label: string; icon: typeof LayoutDashboard; group: string; master?: boolean; founderOrMaster?: boolean };

const NAV: NavItem[] = [
  // ── Core Engines — Flagship AI Engines ──────────────────────────────────────
  { id: 'reality-translator',   label: 'Reality Intelligence Engine', icon: Brain,   group: 'Core Engines' },
  { id: 'r2-equipment-library', label: 'Equipment Library',           icon: Package, group: 'Core Engines' },
  { id: 'r2-bins-explorer',     label: '100-Storey Digital Twin (BINS)', icon: Building2Icon, group: 'Core Engines' },
  { id: 'r2-epe',             label: 'Equipment Planning (EPE)',  icon: Cpu, group: 'Core Engines' },
  { id: 'r2-sustainability',  label: 'Sustainability Intelligence (SIE)', icon: Leaf, group: 'Core Engines' },
  { id: 'r2-vending-machine', label: 'R2 Vending Machine™',      icon: Zap,  group: 'Core Engines' },
  { id: 'r2-academy',         label: '🎓 R2 Academy',            icon: GraduationCap, group: 'Core Engines' },
  { id: 'r2-certification',  label: 'R2 Certification',         icon: Award,         group: 'Core Engines' },
  { id: 'dashboard', label: 'Dashboard',        icon: LayoutDashboard, group: 'Overview' },
  { id: 'index',    label: 'Platform Directory', icon: Compass,         group: 'Overview' },
  { id: 'assets',    label: 'Assets',           icon: Boxes,           group: 'Inventory' },
  { id: 'buildings', label: 'Buildings',        icon: Building2,       group: 'Inventory' },
  { id: 'racks',     label: 'Racks & IDFs',     icon: Server,          group: 'Inventory' },
  { id: 'cabling',   label: 'Cabling',          icon: Cable,           group: 'ICT' },
  { id: 'projects',  label: 'Projects',         icon: FolderKanban,    group: 'Project' },
  { id: 'managed-services', label: 'Managed CAD/BIM', icon: Briefcase, group: 'Project' },
  { id: 'bim-project-config',    label: 'New BIM Project',        icon: Cpu,   group: 'EOS', founderOrMaster: false },
  { id: 'bim-operator-dashboard', label: 'Operator Dashboard',    icon: Users, group: 'EOS', master: true },
  { id: 'bim-engine',            label: 'R² Generation Engine',  icon: Layers, group: 'EOS', founderOrMaster: false },
  { id: 'marketplace',           label: 'Model Marketplace',      icon: Package, group: 'EOS', founderOrMaster: false },
  { id: 'marketplace-orders',    label: 'My Orders',              icon: ShieldCheck, group: 'EOS', founderOrMaster: false },
  { id: 'marketplace-operator',  label: 'Marketplace Ops',        icon: Activity, group: 'EOS', master: true },
  { id: 'schedule',  label: 'Schedule',         icon: CalendarDays,    group: 'Project' },
  { id: 'labor-timeline', label: 'Labor & Timeline Agent', icon: HardHat, group: 'Project' },
  { id: 'certification-pass', label: 'Certification Pass', icon: ShieldCheck, group: 'Project' },
  { id: 'site-audit', label: 'Site Audit (Mobile)', icon: Camera, group: 'Project' },
  { id: 'markup-checkout', label: 'Markup & Checkout', icon: PencilRuler, group: 'Project' },
  { id: 'clashes',   label: 'Clash Detection',  icon: AlertTriangle,   group: 'BIM' },
  { id: 'documents', label: 'Documents',        icon: FileText,        group: 'BIM' },
  { id: 'doc-control', label: 'Document Control', icon: FileLock2,     group: 'BIM' },
  { id: 'r2-brain',   label: 'R2 Brain',         icon: HeartPulse,      group: 'Intelligence' },
  { id: 'vending-machine', label: 'Vending Machine', icon: Zap,          group: 'Intelligence' },
  { id: 'r2-voice',   label: 'R2 Voice',         icon: AudioLines,      group: 'Intelligence' },
  { id: 'mie',        label: 'MIE 2.0',           icon: Cpu,             group: 'Intelligence' },
  { id: 'job-queue',  label: 'Job Queue & Media',  icon: Activity,        group: 'Intelligence' },
  { id: 'rail-engine', label: 'Rail Engine',      icon: TrainFront,      group: 'Intelligence' },
  { id: 'marine-cable', label: 'Marine Cable',    icon: Waves,           group: 'Intelligence' },
  { id: 'underground-corridor', label: 'Underground Corridor', icon: Layers, group: 'Intelligence' },
  { id: 'carbon-intelligence', label: 'Carbon Intelligence', icon: Leaf, group: 'Intelligence' },
  { id: 'business-creator', label: 'Business Creator', icon: Rocket, group: 'Intelligence' },
  { id: 'community-builder', label: 'Community Builder', icon: Home,      group: 'Intelligence' },
  { id: 'hospital-generator', label: 'Hospital Generator', icon: Stethoscope, group: 'Intelligence' },
  { id: 'viie', label: 'Vertical Infra (VIIE)', icon: RadioTower, group: 'Intelligence' },
  { id: 'finance', label: 'Personal Finance', icon: Coins, group: 'Finance' },
  { id: 'finance-pro', label: 'Business & ERP Finance', icon: Landmark, group: 'Finance' },
  { id: 'venue-studio', label: 'Venue & Amenity Studio', icon: Sparkles, group: 'Intelligence' },
  { id: 'manufacturing', label: 'Manufacturing Generator', icon: Boxes,  group: 'Intelligence' },
  { id: 'frequency-lab', label: 'Frequency Lab', icon: Radio,           group: 'Intelligence' },
  { id: 'integration-bridge', label: 'Integration Bridge', icon: Cable,  group: 'Intelligence' },
  { id: 'bunker-builder', label: 'Bunker Builder (Lab)', icon: Sparkles,  group: 'Intelligence' },
  { id: 'r2-news',    label: 'R2 News Feed',     icon: Rss,             group: 'Intelligence', master: true },
  { id: 'r2-academy', label: '🎓 R2 Academy',   icon: GraduationCap,   group: 'Intelligence' },
  { id: 'ai',        label: 'Emmy — Eng. Advisor', icon: Sparkles,        group: 'Intelligence' },
  { id: 'sie',       label: 'Smart Ingest (SIE)',  icon: Wand2,           group: 'Intelligence' },
  { id: 'auto-report', label: 'Auto-Report (Billing)', icon: FileBarChart2,   group: 'Intelligence' },
  { id: 'coach-validation', label: 'Coach Validation', icon: ClipboardCheck, group: 'Intelligence' },
  { id: 'parts-library', label: 'Parts Library',   icon: Package,         group: 'Intelligence' },
  { id: 'comms',     label: 'Comms (Email)',    icon: Mail,            group: 'Intelligence' },
  { id: 'autopilot', label: 'Autopilot (B5)',   icon: Sparkles,        group: 'Intelligence' },
  { id: 'agent',     label: 'Agent Projects',     icon: Bot,          group: 'Intelligence' },
  { id: 'cost',      label: 'Cost Templates (B8)', icon: Wallet,       group: 'Intelligence' },
  { id: 'monitor',   label: 'Monitor View',      icon: Activity,        group: 'Intelligence' },
  { id: 'critical-infra', label: 'Critical Infrastructure', icon: Shield, group: 'Intelligence' },
  { id: 'intelligence', label: 'Industry Intelligence & Ads', icon: Newspaper, group: 'Intelligence' },
  { id: 'estimator', label: 'RCD² Estimator Engine', icon: Calculator, group: 'Intelligence' },
  { id: 'civil', label: 'Civil Ductbank & Manhole', icon: RouteIcon, group: 'Intelligence' },
  { id: 'sync',      label: 'Sync · Folder Monitor', icon: RefreshCw,   group: 'System' },
  { id: 'field',     label: 'Field Ops (B-FIELD)', icon: QrCode,        group: 'System' },
  { id: 'propagation', label: 'Change Propagation (GCPE)', icon: GitCommit, group: 'System' },
  { id: 'health',    label: 'Health (P1.4)',    icon: Activity,        group: 'System' },
  { id: 'backup',    label: 'Backups (B9)',     icon: ShieldCheck,     group: 'System' },
  { id: 'pricing',   label: 'License & Subscription', icon: CreditCard, group: 'System' },
  { id: 'founder-approvals', label: 'Founder Approvals', icon: Inbox, group: 'System', founderOrMaster: true },
  { id: 'investor-relations', label: 'Investor Relations', icon: TrendingUp, group: 'System', founderOrMaster: true },
  { id: 'marketing-controls', label: 'Marketing & Display', icon: Megaphone, group: 'System', founderOrMaster: true },
  { id: 'quote-generator', label: 'R2 Quote Generator', icon: FileText, group: 'System', founderOrMaster: true },
  { id: 'help',      label: 'Support & Feedback', icon: LifeBuoy,      group: 'System' },
  { id: 'settings',  label: 'Settings',         icon: Settings,        group: 'System' },
  { id: 'creator',   label: 'About the Creator', icon: User,           group: 'System' },
  { id: 'feature-flags', label: 'Feature Flags', icon: Layers,         group: 'System', founderOrMaster: true },
  { id: 'global-map',    label: 'Global Map',     icon: Compass,        group: 'System', founderOrMaster: true },
  // Creative Intelligence group
  { id: 'cie',           label: 'R2 Daily Adventures', icon: Sparkles,   group: 'Creative Intelligence' },
  // Automation Studio group
  { id: 'automation-studio',      label: 'Automation Studio',    icon: Zap,         group: 'R2 Platform' },
  { id: 'r2-ai-coach',            label: 'R2 AI Coach',          icon: Brain,        group: 'R2 Platform' },
  { id: 'r2-pie',                 label: 'Personal Intelligence (PIE)', icon: Brain,  group: 'R2 Platform' },
  { id: 'r2-permit-intelligence', label: 'Permit Intelligence (PIE)',   icon: FileText, group: 'R2 Platform' },
  { id: 'r2-core-map',            label: 'R2 Core Ecosystem Map',       icon: Compass,  group: 'R2 Platform', founderOrMaster: true },
  { id: 'r2-finance-hub',         label: 'Finance Hub (FIP)',    icon: TrendingUp,   group: 'R2 Platform' },
  { id: 'r2-calendar',            label: 'Project Calendar',     icon: CalendarDays, group: 'R2 Platform' },
  { id: 'r2-safety',              label: 'Safety Intelligence',  icon: Shield,       group: 'R2 Platform' },
  { id: 'r2-financial-command',   label: 'Financial Command',    icon: TrendingUp,   group: 'R2 Platform' },
  { id: 'r2-timesheet',           label: 'Timesheet Automation', icon: Clock,        group: 'R2 Platform' },
  { id: 'r2-mission-control',     label: 'Mission Control',      icon: Activity,     group: 'R2 Platform' },
  { id: 'r2-marketplace',           label: 'R2 Marketplace',         icon: ShoppingCart,    group: 'R2 Platform' },
  { id: 'r2-vending-machine',       label: 'Order Drawings (VM)',     icon: Zap,             group: 'R2 Platform' },
  { id: 'r2-drawing-generator',     label: 'AI Drawing Generator',   icon: FileText,        group: 'R2 Platform' },
  { id: 'r2-design-package-generator', label: 'AI Design Package',   icon: PencilRuler,     group: 'R2 Platform' },
  { id: 'r2-delivery-portal',       label: 'Order Delivery Portal',  icon: Package,         group: 'R2 Platform' },
  { id: 'r2-customer-dashboard',    label: 'My Dashboard',           icon: LayoutDashboard, group: 'R2 Platform' },
  { id: 'r2-gis-intelligence',      label: 'GIS / Civil Intelligence',icon: Globe,          group: 'R2 Platform' },
  { id: 'r2-intelligent-building',  label: '100-Storey Digital Twin', icon: Building2Icon,  group: 'R2 Platform' },
  { id: 'r2-bins-explorer',         label: 'BINS Explorer',           icon: Building2Icon,  group: 'R2 Platform' },
  { id: 'r2-epe',                   label: 'Equipment Planning (EPE)', icon: Stethoscope,    group: 'R2 Platform' },
  { id: 'r2-sustainability',        label: 'Sustainability (SIE)',     icon: Leaf,           group: 'R2 Platform' },
  { id: 'r2-ai-gateway',          label: 'R2 AI Gateway',        icon: Zap,          group: 'R2 Platform' },
  { id: 'r2-memory-engine',       label: 'R2 Living Memory',     icon: Brain,        group: 'R2 Platform' },
  { id: 'r2-globe',               label: 'Infra Globe',          icon: Globe,        group: 'R2 Platform' },
  { id: 'r2-infra-journey',       label: 'Infra Journey',        icon: Layers,       group: 'R2 Platform' },
  { id: 'r2-digital-twin-library',label: 'Digital Twin Library', icon: Box,          group: '3D Intelligence' },
  { id: 'r2-bim-studio',         label: 'R2 BIM Studio',        icon: Layers,       group: '3D Intelligence' },
  { id: 'r2-global-ops-center',  label: 'Global Ops Center',    icon: Monitor,      group: 'Mission Control' },
  // Creative Studio
  { id: 'r2-creative-studio',       label: 'Creative Studio Hub',      icon: Sparkles,     group: 'Creative Studio' },
  // Creative Studio — Founder-protected experimental engines
  { id: 'r2-scse',                  label: 'Strategy & Simulation',    icon: Globe,        group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-csie',                  label: 'Scene Intelligence',       icon: ScanEye,      group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-ece',                   label: 'Epic Cinema Engine',       icon: Film,         group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-vce',                   label: 'Virtual Cinematography',   icon: Camera,       group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-pce',                   label: 'Performance & Choreo',     icon: Music,        group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-wbe',                   label: 'World Builder',            icon: Layers,       group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-aie',                   label: 'Asset Intelligence',       icon: Package,      group: 'Creative Studio' },
  { id: 'r2-character-creator',     label: '3D Character Creator',     icon: User,         group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-production-studio',     label: 'Virtual Production',       icon: Video,        group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-studio',                label: 'R2 8K Studio',             icon: Clapperboard, group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-cinematic-scene',       label: 'Cinematic Scene Gen',      icon: Film,         group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-voice-engine',          label: 'Voice Experience',         icon: Mic,          group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-role-agent',            label: 'Role Agent Engine',        icon: Brain,        group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-calendar-engine',       label: 'Calendar Engine',          icon: CalendarDays, group: 'Creative Studio' },
  // New Founder-exclusive engines
  { id: 'r2-gle',                   label: 'Global Localization',      icon: Globe,        group: 'Creative Studio' },
  { id: 'r2-mvie',                  label: 'Music Video Intelligence', icon: Video,        group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-aiqe',                  label: 'Audio Intelligence',       icon: AudioLines,   group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-ctee',                  label: 'Civilization Evolution',   icon: Rocket,       group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-arie',                  label: 'Arena Intelligence',       icon: Zap,          group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-vie',                   label: 'Visual Intelligence',      icon: ScanEye,      group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-ree',                   label: 'R2 Economy Engine',        icon: Coins,        group: 'Creative Studio' },
  { id: 'r2-ccfe',                  label: 'Content Classification',   icon: Clapperboard, group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-lie',                   label: 'Life Intelligence',         icon: Leaf,         group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-cate',                  label: 'Catastrophe Engine',        icon: AlertTriangle,group: 'Creative Studio', founderOrMaster: true },
  { id: 'r2-founder-command-center',label: 'Founder Command Centre',   icon: Shield,       group: 'Creative Studio', founderOrMaster: true },
  // 3D Intelligence
  { id: 'r2-3d-engine',             label: 'R2 3D Intelligence',       icon: Box,          group: '3D Intelligence' },
  { id: 'r2-smart-viewer',          label: 'SmartViewer 3D Twin',      icon: Layers,       group: '3D Intelligence' },
  { id: 'r2-isle',                  label: 'Sheet Layout Engine (ISLE)', icon: FileDown,    group: '3D Intelligence' },
  { id: 'r2-mpce',                  label: 'Product Catalog (MPCE)',    icon: Package,     group: 'ICT Intelligence Engines' },
  { id: 'r2-wiie',                  label: 'Wireless Infrastructure (WIIE)', icon: Wifi,   group: 'ICT Intelligence Engines' },
  { id: 'r2-lvtde',                 label: 'LV Typical Details (LVTDE)', icon: BookOpen,   group: 'ICT Intelligence Engines' },
  { id: 'r2-lice',                  label: 'Legal & Compliance (LICE)', icon: Scale,       group: 'ICT Intelligence Engines' },
  { id: 'r2-uiie',                  label: 'Underground Infra (UIIE)',  icon: Map,          group: 'ICT Intelligence Engines' },
  { id: 'r2-ctie',                  label: 'Cable Tray Engine (CTIE)',  icon: Cable,        group: 'ICT Intelligence Engines' },
  { id: 'r2-mfie',                  label: 'Modular Facility (MFIE)',   icon: Building2Icon, group: 'ICT Intelligence Engines' },
  { id: 'r2-ctdte',                 label: 'Commissioning & DT (CTDTE)', icon: ClipboardCheck, group: 'ICT Intelligence Engines' },
  { id: 'r2-meeting-intelligence',  label: 'Meeting Intelligence (MIE)', icon: MessageSquare,  group: 'ICT Intelligence Engines' },
  { id: 'r2-permissions',           label: 'Role Permissions',           icon: Shield,         group: 'ICT Intelligence Engines' },
  { id: 'r2-ase',                   label: 'Sound Effects Engine (ASE)', icon: Music,         group: 'Creative Studio' },
  { id: 'r2-arch-engine',           label: 'Arch Engine (AI → 11)',    icon: Cpu,          group: '3D Intelligence' },
  { id: 'r2-dc-profile',            label: 'DC Intelligence Profile',  icon: Building2,    group: '3D Intelligence' },
  { id: 'r2-annotations',           label: 'Smart Annotation Engine',  icon: PencilRuler,  group: '3D Intelligence' },
  { id: 'bim-engine',               label: 'BIM Generation Engine',    icon: Cpu,          group: '3D Intelligence' },
  { id: 'marketplace-3d-models',    routeId: 'marketplace', label: '3D Model Marketplace', icon: Package, group: '3D Intelligence' },
  // Workspace Tools
  { id: 'r2-measurement-engine',    label: 'Smart Measurement Engine', icon: Ruler,        group: 'Workspace Tools' },
  { id: 'r2-wsie',                  label: 'Workspace Sticky Notes',   icon: StickyNote_,  group: 'Workspace Tools' },
  { id: 'r2-iie',                   label: 'Input Intelligence Engine',icon: Keyboard,     group: 'Workspace Tools' },
  { id: 'r2-bwse',                  label: 'BIM World Simulation',     icon: Building2,    group: 'BIM World Intelligence', founderOrMaster: true },
  { id: 'r2-rae',                   label: 'Revit Adaptive Engine',    icon: Layers,       group: 'BIM World Intelligence', founderOrMaster: true },
  { id: 'r2-r2dl',                  label: 'R2DL Design Language',     icon: Layers,       group: 'BIM World Intelligence', founderOrMaster: true },
  { id: 'r2-dcre',                  label: 'Resilience Engine',        icon: AlertTriangle,group: 'BIM World Intelligence', founderOrMaster: true },
  { id: 'r2-old-earth-world',      label: 'Old Earth World',          icon: Globe,        group: 'BIM World Intelligence', founderOrMaster: false },
];

export function Sidebar({ route, onNavigate }: SidebarProps): JSX.Element {
  const { t } = useLocale();
  const features = useFeatureRegistry();
  const { prefs } = useWorkspacePrefs();
  const brand = getBrand();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const userEmail = user?.email;
  const masterAdmin = Boolean(user?.role === 'Founder' || user?.roles?.includes('Founder'));
  const founderActive = useFounderMode((s) => s.active);
  const { licenseStatus } = useLicense();
  const gkUnseen = useGatekeeperAlertStore((s) => s.unseenCount);
  const dashStats = useDashboardStats();
  const projectCount = dashStats.data?.projects ?? 0;
  const flagsStore = useFeatureFlagsStore();

  // Derived display values
  const displayName = user
    ? ([user.firstName, user.lastName].filter(Boolean).join(' ') || userEmail?.split('@')[0] || 'User')
    : 'Guest';

  const tierEdition = licenseStatus?.tier ? TIER_EDITION[licenseStatus.tier] : undefined;

  const isRcddVerified =
    licenseStatus?.status === 'verified' &&
    (licenseStatus.tier === 'contractor' || licenseStatus.tier === 'designer') &&
    !!licenseStatus.bicsiId;

  // Minimal Portable Mode — collapse the navigation to just the empty-state
  // Dashboard and the Settings page where the user can toggle it back off.
  const NAV_VISIBLE = (prefs.minimalMode
    ? NAV.filter((n) => n.id === 'dashboard' || n.id === 'settings' || n.id === 'creator')
    : NAV
  ).filter((n) =>
    (!n.master || masterAdmin) &&
    (!n.founderOrMaster || masterAdmin || founderActive) &&
    (n.id !== 'pricing' || prefs.showPricing) &&
    flagsStore.isVisible(n.id, founderActive || masterAdmin, masterAdmin)
  );
  const NAV_LOCALISED: typeof NAV = NAV_VISIBLE.map((n) => {
    if (n.id === 'dashboard') return { ...n, label: t('nav.dashboard') };
    if (n.id === 'cost')      return { ...n, label: t('nav.cost') };
    if (n.id === 'backup')    return { ...n, label: t('nav.backup') };
    if (n.id === 'health')    return { ...n, label: t('nav.health') };
    if (n.id === 'settings')  return { ...n, label: t('nav.settings') };
    return n;
  });
  const groups = Array.from(new Set(NAV_LOCALISED.map((n) => n.group)));
  const favorites = useFavorites();

  // ── Collapsible nav groups (persisted) — keeps the sidebar snug on one viewport.
  const COLLAPSE_KEY = 'r2.nav.collapsedGroups';
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '[]') as string[]); }
    catch { return new Set<string>(); }
  });
  const toggleGroup = (g: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch { /* no-op */ }
      return next;
    });
  };

  return (
    <aside
      data-testid="sidebar"
      className="w-[260px] shrink-0 flex flex-col"
      style={{
        background:          'rgba(8, 16, 36, 0.82)',
        backdropFilter:      'blur(20px)',
        WebkitBackdropFilter:'blur(20px)',
        borderRight:         '1px solid rgba(37,181,168,0.13)',
        position:            'relative',
        zIndex:               10,
      }}
    >
      <div data-testid="brand-header" className="px-5 py-5"
        style={{ borderBottom: '1px solid rgb(var(--border)/0.4)' }}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="brand-monogram"
            onClick={() => { void logout(); }}
            title="Exit to the public Home / Login page"
            className="h-10 w-10 rounded-xl grid place-items-center transition-all hover:brightness-125"
            style={{
              background: 'rgba(20,184,166,0.10)',
              border: '1px solid rgba(20,184,166,0.30)',
              boxShadow: '0 0 18px rgba(20,184,166,0.08)',
              cursor: 'pointer',
            }}
            aria-label="Exit to public Home / Login page"
          >
            <LogoMark size={28} className="text-teal-400" />
          </button>
          <div className="flex flex-col leading-tight gap-[4px]">
            <span
              data-testid="brand-product-name"
              className="text-ui text-[13px] font-extrabold tracking-[0.16em]"
              style={{ color: 'rgb(var(--fg))' }}
            >
              {brand.productName}
            </span>
            {/* Tier indicator — Founder Edition overrides all when active */}
            {founderActive ? (
              <FounderEditionBadge size="sm" />
            ) : masterAdmin ? (
              <span
                data-testid="brand-tier-edition"
                style={{
                  fontSize:      7.5,
                  fontWeight:    800,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color:         'rgba(252,211,77,0.9)',
                  textShadow:    '0 0 8px rgba(252,211,77,0.45)',
                  lineHeight:    1,
                }}
              >
                Founder's Mode
              </span>
            ) : tierEdition ? (
              <span
                data-testid="brand-tier-edition"
                style={{
                  fontSize:      7,
                  fontWeight:    700,
                  letterSpacing: '0.20em',
                  textTransform: 'uppercase',
                  color:         tierEdition.color,
                  lineHeight:    1,
                }}
              >
                {tierEdition.label}
              </span>
            ) : (
              <span
                data-testid="brand-tier-edition"
                style={{
                  fontSize:      7,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color:         'rgba(148,172,210,0.28)',
                  lineHeight:    1,
                }}
              >
                Trial
              </span>
            )}
          </div>
          <div className="ml-auto"><MiniEcgMonitor projectCount={projectCount} energized={prefs.waveMode} onOpen={() => onNavigate('r2-brain')} /></div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {/* ── Favorites Bar ────────────────────────────────────────────────── */}
        {favorites.length > 0 && (
          <div className="mb-3 px-2">
            <div className="flex items-center gap-1.5 px-3 mb-1.5" style={{ color: 'rgba(252,211,77,0.6)' }}>
              <Star className="h-2.5 w-2.5" />
              <span className="text-[10px] uppercase tracking-[0.22em]">Favorites</span>
            </div>
            <ul className="px-0 space-y-0.5">
              {favorites.map((fav) => {
                const active = route === fav.route;
                return (
                  <li key={fav.id}>
                    <button
                      data-testid={`nav-fav-${fav.id}`}
                      onClick={() => onNavigate(fav.route as Route)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all border-l-2"
                      style={{
                        color: active ? 'rgb(var(--fg))' : 'rgba(252,211,77,0.7)',
                        background: active ? 'rgba(252,211,77,0.08)' : 'transparent',
                        borderLeftColor: active ? 'rgba(252,211,77,0.8)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(252,211,77,0.06)'; }}
                      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Star className="h-3 w-3 shrink-0" style={{ color: 'rgba(252,211,77,0.55)' }} />
                      <span className="truncate text-left">{fav.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mx-3 mt-2 mb-1 h-px" style={{ background: 'rgba(252,211,77,0.12)' }} />
          </div>
        )}
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group);
          const groupItems = NAV_LOCALISED.filter((n) => n.group === group);
          const hasActive = groupItems.some((n) => n.id === route);
          return (
          <div
            key={group}
            data-testid={`nav-group-${group.toLowerCase().replace(/\s+/g, '-')}`}
            className="mb-3"
          >
            <button
              type="button"
              data-testid={`nav-group-toggle-${group.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => toggleGroup(group)}
              className="w-full flex items-center justify-between px-5 mb-1.5 group/nav transition-colors"
              style={{ color: group === 'Core Engines' ? 'rgba(245,158,11,0.7)' : 'rgb(var(--muted)/0.5)' }}
              title={isCollapsed ? `Expand ${group}` : `Collapse ${group}`}
            >
              <span className="text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5">
                {group === 'Core Engines' && <Sparkles className="h-3 w-3" />}
                {group}
                {isCollapsed && hasActive && (
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-400" title="Active page inside" />
                )}
              </span>
              <ChevronDown
                className="h-3 w-3 transition-transform group-hover/nav:text-teal-400"
                style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                strokeWidth={2}
              />
            </button>
            {!isCollapsed && (
            <ul className="px-2 space-y-0.5">
              {groupItems.map((n) => {
                const routeId = (n as any).routeId ?? n.id;
                const active = route === routeId;
                const Icon = n.icon;
                const featureState  = features.state(routeId);
                const interactive   = features.isInteractive(routeId);
                const entry         = features.byId(routeId);
                const tooltip       = entry?.inactiveTooltip ?? entry?.description;
                const groupKey = n.group.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                return (
                  <li key={n.id}>
                    <button
                      data-testid={`nav-${groupKey}-${n.id}`}
                      data-route-testid={`nav-${routeId}`}
                      data-feature-state={featureState}
                      onClick={() => { if (interactive) onNavigate(routeId); }}
                      disabled={!interactive}
                      title={tooltip}
                      className={[
                        'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all',
                        active
                          ? 'border-l-2'
                          : interactive
                            ? 'hover:bg-[rgb(var(--panel-2))] border-l-2 border-transparent'
                            : 'cursor-not-allowed border-l-2 border-transparent opacity-50',
                        featureState === 'EXPERIMENTAL'
                          ? '!border-l-2 !border-sky-400/50'
                          : '',
                      ].join(' ')}
                      style={{
                        color: active
                          ? 'var(--nl-text-primary, rgb(var(--fg)))'
                          : interactive
                          ? 'rgb(var(--muted))'
                          : 'rgb(var(--muted)/0.5)',
                        background: active
                          ? (n.group === 'Core Engines' ? 'rgba(245,158,11,0.10)' : 'var(--nl-panel-glow, rgba(20,184,166,0.10))')
                          : undefined,
                        borderLeftColor: active
                          ? (n.group === 'Core Engines' ? 'rgba(245,158,11,0.8)' : 'var(--nl-tab-underline, rgb(20,184,166))')
                          : 'transparent',
                        textShadow: active
                          ? 'var(--nl-tab-glow, none)'
                          : 'none',
                        boxShadow: active
                          ? (n.group === 'Core Engines'
                              ? 'inset 0 0 12px rgba(245,158,11,0.06)'
                              : `inset 0 0 12px var(--nl-panel-glow, rgba(20,184,166,0.06))`)
                          : 'none',
                      }}
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                      <span className="flex-1 text-left text-ui">{n.label}</span>
                      {n.id === 'r2-brain' && (
                        <span data-testid="r2-brain-pulse" className="relative flex h-2 w-2 mr-0.5" title="R2 Brain is live">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-70 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-400" />
                        </span>
                      )}
                      <FeatureBadge state={featureState} />
                    </button>
                  </li>
                );
              })}
            </ul>
            )}
          </div>
          );
        })}
      </nav>

      {/* Global Command Centre — Master Admin only ───────────────────────── */}
      {masterAdmin && (
        <div className="px-4 pb-2">
          <button
            onClick={() => onNavigate('global-command')}
            data-testid="nav-global-command"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all text-left"
            style={{
              background: route === 'global-command'
                ? 'rgba(245,158,11,0.12)'
                : 'rgba(245,158,11,0.06)',
              border: route === 'global-command'
                ? '1px solid rgba(245,158,11,0.35)'
                : '1px solid rgba(245,158,11,0.18)',
              boxShadow: route === 'global-command'
                ? '0 0 12px rgba(245,158,11,0.12)'
                : 'none',
            }}
          >
            <Crown className="h-3.5 w-3.5 text-amber-400" strokeWidth={1.6} />
            <span className="text-[11px] font-semibold tracking-wide text-amber-400">
              Global Command Centre
            </span>
            {gkUnseen > 0 && (
              <span
                data-testid="gcc-gatekeeper-badge"
                className="ml-auto flex items-center justify-center rounded-full text-[10px] font-bold"
                style={{
                  minWidth: 18, height: 18, padding: '0 5px',
                  background: '#f43f5e', color: '#fff',
                  boxShadow: '0 0 10px rgba(244,63,94,0.6)',
                  animation: 'gkBadgePulse 1.6s ease-in-out infinite',
                }}
                title={`${gkUnseen} new blocked attempt${gkUnseen === 1 ? '' : 's'}`}
              >
                {gkUnseen > 99 ? '99+' : gkUnseen}
                <style>{`@keyframes gkBadgePulse{0%,100%{transform:scale(1);opacity:1;}50%{transform:scale(1.12);opacity:0.85;}}`}</style>
              </span>
            )}
          </button>
        </div>
      )}

      {/* Footer: dynamic user credential + version ─────────────────────── */}
      <div
        className="px-5 pt-3 pb-3 flex flex-col gap-1.5"
        style={{ borderTop: '1px solid rgb(var(--border)/0.4)' }}
      >
        <div className="mb-1.5"><LastMileTracer /></div>
        {/* Credential line — dynamic based on verified status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {isRcddVerified && licenseStatus?.bicsiId ? (
            <>
              <ShieldCheck
                style={{ width: 9, height: 9, color: 'rgba(94,234,212,0.60)', flexShrink: 0 }}
                strokeWidth={2.5}
              />
              <span
                data-testid="sidebar-credential-line"
                style={{ fontSize: 9, color: 'rgba(148,172,210,0.52)', letterSpacing: '0.04em', lineHeight: 1.3 }}
              >
                Drafted by{' '}
                <span style={{ color: 'rgba(148,172,210,0.82)' }}>{displayName}</span>
                {', RCDD #' + formatBicsiDisplay(licenseStatus.bicsiId)}
              </span>
            </>
          ) : (
            <span
              data-testid="sidebar-credential-line"
              style={{ fontSize: 9, color: 'rgba(148,172,210,0.35)', letterSpacing: '0.04em', lineHeight: 1.3 }}
            >
              {displayName}
              {' · '}
              {licenseStatus?.tier ? (TIER_LABEL[licenseStatus.tier] ?? licenseStatus.tier) : 'Trial'} User
            </span>
          )}
        </div>

        {/* You are here — URL breadcrumb */}
        <div
          data-testid="sidebar-url-breadcrumb"
          style={{
            background: 'rgba(6,12,26,0.7)',
            border: '1px solid rgba(30,50,80,0.5)',
            borderRadius: 8, padding: '7px 10px', marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em',
            color: 'rgba(45,181,168,0.45)', marginBottom: 3 }}>
            Current Page
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <code style={{ fontSize: 10, color: 'rgba(251,191,36,0.7)', fontFamily: 'monospace',
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              #{route}
            </code>
            <button
              data-testid="sidebar-copy-url-btn"
              title="Copy shareable link"
              onClick={() => {
                const url = `${window.location.origin}/#${route}`;
                navigator.clipboard.writeText(url).catch(() => {});
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                color: 'rgba(45,181,168,0.45)', display: 'flex', alignItems: 'center' }}
            >
              <Copy size={10} />
            </button>
          </div>
        </div>

        {/* Version line */}
        <div
          className="flex items-center justify-between font-mono"
          style={{ color: 'rgb(var(--muted)/0.38)' }}
        >
          <span style={{ fontSize: 10 }}>v{brand.version ?? '1.0.0'}</span>
          <span style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{brand.shortName}</span>
        </div>
      </div>
    </aside>
  );
}
